import React from 'react';

import { Divider, Skeleton } from 'antd';
import {
  Avatar,
  AvatarShape,
  Button,
  ButtonType,
  Column,
  Icon,
  LoadingOutlined,
  Notification,
  Row,
  Spacing,
  Text,
} from 'farmhub-ui-core';
import QRCode from 'qrcode.react';
import { useScreenshot } from 'use-react-screenshot';

import { useAppDispatch, useAppSelector } from 'app';
import { InfoCard } from 'components';
import { CopyOutlineIcon, DownloadOutlineIcon } from 'components/Icon';
import { env } from 'env';
import {
  findOneOrderByOrderCode,
  selectAppDevice,
  selectAppState,
} from 'slice';
import { theme } from 'styled';
import {
  copyLink,
  DEFAULT_LOGO_SKELETON,
  formatImageUrl,
  formatMoney,
  getBase64Image,
  handleExportImage,
  IMAGETYPE,
  ORDER_TASK_TYPE,
} from 'utils';

import {
  DesktopContentWrapper,
  DesktopLinkWrapper,
  DesktopQRCodeWrapper,
  DesktopSupplierLogo,
  DesktopWrapper,
} from './styles';

type QRCodeSharingDesktopProps = {
  order: any;
};

const QRCodeSharingDesktop = ({
  order,
}: QRCodeSharingDesktopProps): JSX.Element => {
  const appDevice = useAppSelector(selectAppDevice);
  const dispatch = useAppDispatch();
  const app = useAppSelector(selectAppState);
  const QRCodeRef = React.useRef<HTMLInputElement>(null);
  const entityID = useAppSelector(state => state.account.item?.id);
  const entityType = useAppSelector(state => state.account.item?.type);
  const [isLoading, setIsLoading] = React.useState(true);
  const [data, setData] = React.useState<any>({});
  const [linkLogo, setLinkLogo] = React.useState<string>();
  const [link, setLink] = React.useState<any>();
  const [name, setName] = React.useState<string>();
  const [isDownloadQRCode, setIsDownloadQRCode] = React.useState(false);
  const [isCopyingQRCode, setIsCopyingQRCode] = React.useState(false);
  const [image, takeScreenShot] = useScreenshot();

  const findOrderInfo = async (): Promise<void> => {
    try {
      if (entityType === undefined || !entityID) {
        return;
      }
      setIsLoading(true);

      const params = {
        orderCode: order?.orderID,
      };

      const result: any = await dispatch(
        findOneOrderByOrderCode(params)
      ).unwrap();

      const { data: orderData } = result;

      setData(orderData);
      if (order?.type === ORDER_TASK_TYPE.PURCHASE_ORDER) {
        setLinkLogo(orderData.supplier?.avatar);
        setName(orderData.supplier?.displayName);
      } else {
        setLinkLogo(orderData.customer?.avatar);
        setName(orderData.customer?.displayName);
      }

      setLink(
        `${app?.domains?.hubBilling?.domain ?? env.service.hubbillingWebApp}/${
          orderData?.code
        }`
      );
    } catch (error) {
      console.log(
        'An error occurred while getting order info/ order logs',
        error
      );
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    if (order?.orderID) {
      findOrderInfo();
    }
  }, [dispatch, entityID, order?.orderID, entityType]);

  const handleLoadImage = (
    sourceElementID: string,
    resultElementID: string
  ): void => {
    setTimeout(() => {
      const imageSource: HTMLImageElement | null = document.getElementById(
        sourceElementID
      ) as HTMLImageElement;

      const imageResult: HTMLImageElement | null = document.getElementById(
        resultElementID
      ) as HTMLImageElement;

      if (imageSource && imageResult) {
        imageResult.src = getBase64Image(imageSource);
      }
    }, 1);
  };

  const handleDownloadQRCode = async () => {
    setIsDownloadQRCode(true);
    const imageName =
      order?.type === ORDER_TASK_TYPE.PURCHASE_ORDER
        ? `ma qr đơn nhập hàng ${data?.code}`
        : `ma qr đơn hàng ${data?.code}`;
    await handleExportImage({
      imageName,
      imageType: 'png',
      ref: QRCodeRef,
    });
    setIsDownloadQRCode(false);
  };

  const handleCopy = async () => {
    if (QRCodeRef.current) {
      setIsCopyingQRCode(true);

      const copy = async (img: any) => {
        try {
          const response = await fetch(img);
          const blob: any = await response.blob();
          await window.navigator.clipboard.write([
            new ClipboardItem({
              [blob.type]: blob,
            }),
          ]);
        } catch (err: any) {
          Notification(
            'error',
            'Sao chép mã QR thất bại',
            undefined,
            appDevice.device
          );
        }
      };

      await takeScreenShot(QRCodeRef.current).then(copy);
      setIsCopyingQRCode(false);
    }
  };

  return (
    <DesktopWrapper>
      <Column styles={{ gap: 16 }}>
        <Text color="primary" weight="bold">
          Mã QR Code:
        </Text>
        <DesktopContentWrapper>
          <Row
            styles={{
              padding: '16px',
              border: '0px',
              borderRadius: '6px',
            }}
            width="100%"
          >
            {isLoading ? (
              <Spacing>
                <Skeleton.Avatar
                  shape="circle"
                  style={{
                    width: theme.avatar.size.md,
                    height: theme.avatar.size.md,
                  }}
                />

                <Column>
                  <Skeleton.Input size="small" style={{ width: 150 }} />
                  <Skeleton.Input size="small" style={{ width: 150 }} />
                </Column>
              </Spacing>
            ) : (
              <InfoCard
                img={
                  <Avatar
                    shape={AvatarShape.Circle}
                    size={theme.avatar.size.md}
                    src={formatImageUrl(linkLogo, IMAGETYPE.AVATAR.xxs) ?? ''}
                    styles={{ 'flex-shrink': 0 }}
                  />
                }
                info={
                  <Row>
                    <Text color="main" lineNumber={1} size="xxs">
                      Tổng tiền:{' '}
                      <Text color="price" size="xxs" weight="bold">
                        {formatMoney(data?.totalPrice)}đ
                      </Text>
                    </Text>
                  </Row>
                }
                name={
                  <Text
                    lineHeight="22px"
                    lineNumber={1}
                    title={name ?? ''}
                    weight="bold"
                  >
                    {name ?? ''}
                  </Text>
                }
                width="100%"
              />
            )}
          </Row>
          <Column
            alignItems="center"
            justifyContent="center"
            styles={{
              borderTop: '1px solid #d9d9d9',
              padding: '16px 24px',
              gap: 16,
            }}
          >
            <DesktopQRCodeWrapper ref={QRCodeRef}>
              <QRCode
                level="L"
                renderAs="svg"
                style={{ height: '280px', width: '280px' }}
                value={`${
                  app?.domains?.hubBilling?.domain ??
                  env.service.hubbillingWebApp
                }/${data?.code}`}
              />
              <div className="face-logo">
                <img
                  alt="avatar"
                  crossOrigin="anonymous"
                  id="imageSource"
                  src={
                    linkLogo
                      ? formatImageUrl(
                          String(linkLogo),
                          IMAGETYPE.AVATAR.xxs,
                          true
                        )
                      : DEFAULT_LOGO_SKELETON
                  }
                  style={{
                    display: 'none',
                  }}
                  onLoad={() => handleLoadImage('imageSource', 'imageResult')}
                />
                <DesktopSupplierLogo
                  alt="avatar"
                  crossOrigin="anonymous"
                  id="imageResult"
                  src={
                    linkLogo
                      ? formatImageUrl(
                          String(linkLogo),
                          IMAGETYPE.AVATAR.xxs,
                          true
                        )
                      : DEFAULT_LOGO_SKELETON
                  }
                />
              </div>
            </DesktopQRCodeWrapper>
            <Row justifyContent="center" width="fit-content">
              <Icon
                tooltip="Tải QR Code"
                onClick={() => {
                  handleDownloadQRCode();
                }}
              >
                {isDownloadQRCode ? (
                  <LoadingOutlined
                    style={{ color: theme.colors.primary.main, fontSize: 18 }}
                  />
                ) : (
                  <DownloadOutlineIcon
                    color={theme.colors.primary.main}
                    height={18}
                    width={18}
                  />
                )}
              </Icon>
              <Divider type="vertical" />
              <Icon
                tooltip="Sao chép QR Code"
                onClick={() => {
                  handleCopy();
                }}
              >
                {isCopyingQRCode ? (
                  <LoadingOutlined
                    style={{ color: theme.colors.primary.main, fontSize: 18 }}
                  />
                ) : (
                  <CopyOutlineIcon
                    color={theme.colors.primary.main}
                    height={18}
                    width={18}
                  />
                )}
              </Icon>
            </Row>
          </Column>
        </DesktopContentWrapper>
        <DesktopLinkWrapper>
          <Row justifyContent="space-between" styles={{ gap: 6 }}>
            <Text lineHeight="22px" weight="bold">
              {isLoading ? (
                <Skeleton.Input size="small" style={{ width: 350 }} />
              ) : (
                link
              )}
            </Text>
            <Button
              height="42px"
              type={ButtonType.Primary}
              width="fit-content"
              onClick={async () => {
                copyLink(link, 'đường dẫn');
              }}
            >
              Sao chép
            </Button>
          </Row>
        </DesktopLinkWrapper>
      </Column>
    </DesktopWrapper>
  );
};

export default QRCodeSharingDesktop;
